import React from 'react'
import { Autocomplete, TextField } from '@mui/material'
import moment from 'moment'

const DATE = 'YYYY-MM-DD'
const STEP_MINUTES = 5

// Every five minutes across the day, offered as suggestions. Typing is still allowed:
// the list is there to make the common case one click, not to forbid 18:07.
export const TIME_OPTIONS = (() => {
  const options = []
  for (let minutes = 0; minutes < 24 * 60; minutes += STEP_MINUTES) {
    const h = Math.floor(minutes / 60)
    const m = minutes % 60
    options.push((h < 10 ? '0' : '') + h + ':' + (m < 10 ? '0' : '') + m)
  }
  return options
})()

// Accepts what people actually type: 18:30, 8:05, 1830, 830, 18.30.
// Parsed against a fixed date so anything that rolls over ('24:00') is rejected rather
// than quietly becoming 00:00 on the same day.
const TIME_BASE = '2020-01-01'
export const normalizeTime = (raw) => {
  if (raw === 0) return null
  if (!raw) return null
  const trimmed = String(raw).trim()
  const formats = ['H:mm', 'HH:mm', 'H.mm', 'HH.mm', 'Hmm', 'HHmm']
  for (let i = 0; i < formats.length; i++) {
    const parsed = moment(TIME_BASE + ' ' + trimmed, 'YYYY-MM-DD ' + formats[i], true)
    if (parsed.isValid() && parsed.format(DATE) === TIME_BASE) return parsed.format('HH:mm')
  }
  return null
}

/** Time field: a five-minute list you can also type into. */
function TimeSelect({ id, label, value, onChange, sx, helperText, error, required }) {
  const [draft, setDraft] = React.useState(value || '')

  React.useEffect(() => {
    setDraft(value || '')
  }, [value])

  const commit = (raw) => {
    const normalized = normalizeTime(raw)
    // Unparseable input reverts rather than clearing the time that was already set.
    if (normalized) onChange(normalized)
    else setDraft(value || '')
  }

  return (
    <Autocomplete
      freeSolo
      disableClearable
      openOnFocus
      options={TIME_OPTIONS}
      inputValue={draft}
      sx={sx}
      onInputChange={(event, next, reason) => {
        if (reason !== 'reset') setDraft(next)
      }}
      onChange={(event, next) => {
        if (typeof next === 'string') commit(next)
      }}
      onBlur={() => commit(draft)}
      ListboxProps={{ style: { maxHeight: 320 } }}
      renderInput={(params) => (
        <TextField
          {...params}
          id={id}
          required={required !== false}
          label={label}
          size='small'
          error={error}
          helperText={helperText}
        />
      )}
    />
  )
}

export default TimeSelect
